// API calls and types for the booking review queue (api/ams/booking-review). Mirrors
// AmsBookingReviewItem / AmsBookingReviewDetail in Cms.Services.Database. Enums arrive as numbers.

import { api } from "@/lib/api"

/** Mirrors AmsBookingReviewDecision. */
export const ReviewDecision = {
  Pending: 0,
  Keep: 1,
  CancelDuplicate: 2,
} as const

export type ReviewDecisionValue = (typeof ReviewDecision)[keyof typeof ReviewDecision]

/** The AmsBookingReviewReason bit a failed booking-move batch admits a booking with (AB#809). */
export const RESCHEDULE_FAILED_REASON = 32

/**
 * The AmsBookingReviewReason bit the maintenance sweep admits a booking with when it writes off a
 * reassignment that had already cancelled it in Cal (ams-u3b).
 */
export const REASSIGNMENT_WRITE_OFF_REASON = 64

/** Mirrors AmsBookingMoveBatchMode. */
export const BookingMoveBatchMode = {
  Automatic: 0,
  Manual: 1,
} as const

/**
 * A booking-move batch's failed attempt to move this booking (AmsBookingRescheduleFailure, AB#809):
 * which batch, where it was meant to go, and why it did not. Staff only.
 */
export interface RescheduleFailure {
  batchId: string
  /** BookingMoveBatchMode. */
  mode: number
  targetCalUserId: number
  /** The target host's name, when CMS has it on the booking. */
  targetHostName: string | null
  targetHostEmail: string | null
  /** ISO instant. */
  targetStart: string
  failureDetail: string | null
}

/**
 * A reassignment the maintenance sweep wrote off after cancelling this booking in Cal
 * (AmsBookingReassignmentWriteOff, ams-u3b): where it was aiming and why. Staff only.
 */
export interface ReassignmentWriteOff {
  reassignmentId: string
  targetCalUserId: number
  targetHostEmail: string | null
  /** ISO instant. */
  slotStart: string | null
  /** ISO instant. */
  writtenOffAt: string | null
  failureDetail: string | null
}

export interface BookingReviewItem {
  id: string
  referenceCode: string
  status: number
  slotStart: string | null
  siteName: string | null
  attendeeName: string | null
  phoneNumber: string | null
  address: string | null
  reviewReasons: number
  topMatchBand: number
  matchedFields: string | null
  blockedPrograms: number
  isReviewed: boolean
  reviewDecision: number
  createdAt: string
  /**
   * Whether a Social Security number is on file and has not been erased. The digits themselves are
   * never part of this payload — they come only from an explicit reveal, which needs nothing beyond
   * the page's own `appointments:manage` (AB#845) and is audited against the staff member who made it.
   */
  hasSsn: boolean
  /** The host's display name, as Cal had it. */
  hostName?: string | null
  /** The Cal event type the booking was made on ("intake", "off-slot"). */
  eventTypeSlug?: string | null
  /** When the appointment ends, once the sweep has read it back from Cal. */
  slotEnd?: string | null
  /** The Cal booking, once one exists. */
  calBookingUid?: string | null
  /**
   * The household's six-digit confirmation number (AB#772), what the review shows as its confirmation
   * number (AB#797). Null for a request that never got a time, or an older booking not yet backfilled.
   */
  confirmationCode?: string | null
  /** Who recorded the decision, as their UPN (AB#841). Null while the item is pending. */
  decisionMadeBy?: string | null
  /** When the decision was recorded, an ISO instant (AB#841). Null while the item is pending. */
  decisionMadeAt?: string | null
  /** The utility account number the applicant gave, as typed (AB#797). Null when none was entered. */
  energyAccountNumber?: string | null
  /** The reviewer's comments on the recorded decision (AB#797). Null while pending or when none were left. */
  userComments?: string | null
}

export interface BookingCandidate {
  subject: number
  band: number
  matchedFields: string
  matchingAlgorithm: string
  matchingDetails: string
  matchedBookingRequestId: string | null
  matchedBenefitId: string | null
  matchedPersonId: string | null
  // The existing appointment an existing-appointment candidate (subject 0) points at, read from that
  // booking when the detail is asked for, following its reschedules (AB#797). Null for a
  // benefit-application candidate, and for a booking that is gone. Staff only.
  /** The existing appointment's opaque reference code. Not shown; the card shows `matchedConfirmationCode`. */
  matchedReferenceCode?: string | null
  /** The existing appointment's six-digit confirmation number (AB#772). Null when it has none yet. */
  matchedConfirmationCode?: string | null
  /** The existing appointment's client name. */
  matchedAttendeeName?: string | null
  /** When the existing appointment starts, an ISO instant. */
  matchedSlotStart?: string | null
  /** The existing appointment's Cal booking, to open it. Null when it has none. */
  matchedCalBookingUid?: string | null
}

export interface BookingReviewDetail {
  item: BookingReviewItem
  calBookingUid: string | null
  candidates: BookingCandidate[]
  hostName?: string | null
  eventTypeSlug?: string | null
  slotEnd?: string | null
  /** The request's current status; falls back to `item.status` when absent. */
  status?: number | null
  /**
   * The decisions this request accepts, in the order to offer them (AB#809): Keep alone for a failed
   * batch move. An older server leaves it out, which means both.
   */
  allowedDecisions?: number[] | null
  /** For a request flagged "Reschedule failed", the batch, the attempted target and the failure. */
  rescheduleFailure?: RescheduleFailure | null
  /** For a request flagged "Reassignment written off", the reassignment and why it was written off. */
  reassignmentWriteOff?: ReassignmentWriteOff | null
}

const BASE = "/api/ams/booking-review"

/** The queue. Withdrawn bookings are hidden server-side; decided items only with `includeReviewed`. */
export function getReviewQueue(includeReviewed = false): Promise<BookingReviewItem[]> {
  return api.get<BookingReviewItem[]>(includeReviewed ? `${BASE}?includeReviewed=true` : BASE)
}

export function getReviewDetail(requestId: string): Promise<BookingReviewDetail> {
  return api.get<BookingReviewDetail>(`${BASE}/${requestId}`)
}

/**
 * Records a decision. For CancelDuplicate the server cancels the appointment first and keeps the
 * comments as an internal note on the booking.
 */
export function recordReviewDecision(requestId: string, decision: ReviewDecisionValue, comments: string): Promise<unknown> {
  const trimmed = comments.trim()
  return api.post(`${BASE}/${requestId}/decision`, {
    decision,
    comments: trimmed === "" ? null : trimmed,
  })
}

export function revealReviewSsn(requestId: string): Promise<{ ssn: string }> {
  return api.post<{ ssn: string }>(`${BASE}/${requestId}/ssn`, {})
}
